import React from 'react'
import { useLocation, Link } from 'react-router-dom'
import { Text } from '@vibe/core'
import { MoveArrowRight as ChevronRightIcon } from '@vibe/icons'

interface BreadcrumbItem {
  label: string
  path?: string
}

/**
 * Breadcrumb navigation component that shows the current page hierarchy
 */
export const Breadcrumbs: React.FC = () => {
  const location = useLocation()

  // Define breadcrumb mappings for different routes
  const getBreadcrumbs = (pathname: string): BreadcrumbItem[] => {
    const segments = pathname.split('/').filter(Boolean)
    const breadcrumbs: BreadcrumbItem[] = []

    // Always start with Dashboard for authenticated routes
    if (pathname !== '/dashboard') {
      breadcrumbs.push({ label: 'Dashboard', path: '/dashboard' })
    }

    // Special handling for candidate detail pages
    if (segments[0] === 'candidate' && segments[1]) {
      // Get solicitud ID from location state or URL params
      const solicitudId =
        location.state?.solicitudId ||
        new URLSearchParams(location.search).get('solicitud')

      if (solicitudId) {
        // Add breadcrumb that links back to the specific job posting's candidates
        breadcrumbs.push({
          label: 'Candidatos',
          path: `/candidates/${solicitudId}`,
        })
      } else {
        // Fallback to generic candidates label without link
        breadcrumbs.push({ label: 'Candidatos' })
      }

      // Add current page (candidate detail) without link
      breadcrumbs.push({ label: 'Detalle de Candidato' })
      return breadcrumbs
    }

    // Map route segments to breadcrumb labels
    const routeMap: Record<string, string> = {
      dashboard: 'Dashboard',
      'new-job': 'Nueva Solicitud',
      candidates: 'Candidatos',
      candidate: 'Detalle de Candidato',
      job: 'Solicitud',
    }

    segments.forEach((segment, index) => {
      const label = routeMap[segment] || segment
      const isLast = index === segments.length - 1

      // Don't add path for the last item (current page)
      const path = isLast
        ? undefined
        : `/${segments.slice(0, index + 1).join('/')}`

      breadcrumbs.push({ label, path })
    })

    return breadcrumbs
  }

  const breadcrumbs = getBreadcrumbs(location.pathname)

  // Don't show breadcrumbs if there's only one item (current page)
  if (breadcrumbs.length <= 1) {
    return null
  }

  return (
    <nav className="py-3" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {breadcrumbs.map((breadcrumb, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && (
              <ChevronRightIcon
                className="h-4 w-4 text-gray-400 mx-2"
                aria-hidden="true"
              />
            )}

            {breadcrumb.path ? (
              <Link
                to={breadcrumb.path}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Text type="text2" color="secondary">
                  {breadcrumb.label}
                </Text>
              </Link>
            ) : (
              <Text
                type="text2"
                className="font-medium text-gray-900"
                aria-current="page"
              >
                {breadcrumb.label}
              </Text>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}
